import React, { useEffect, useState } from 'react';
import {
    Spin, Button, Tabs, Tag, Descriptions, Table, Popconfirm,
    Modal, Form, Input, Typography, Space, Collapse, App, Image,
} from 'antd';
import {
    ArrowLeftOutlined, WarningOutlined, CheckCircleOutlined,
    DeleteOutlined, UserOutlined, LoadingOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import adminApi from '../../api/adminAxios';
import dayjs from 'dayjs';

const { Text } = Typography;

const verificationLabels = {
    name_dob_status:          'Name / DOB',
    workplace_salary_status:  'Workplace / Salary',
    education_status:         'Education',
    caste_status:             'Caste',
    religion_status:          'Religion',
    house_status:             'House',
    car_status:               'Car',
};

const verificationBadge = (v) => {
    if (v === 1) return <Tag color="green">Verified</Tag>;
    if (v === 0) return <Tag color="orange">Pending</Tag>;
    return <Tag color="default">Not Submitted</Tag>;
};

// Renders a titled block of Descriptions items, skipping null/empty values
function ProfileSection({ title, items }) {
    const visible = items.filter(([, v]) => v != null && v !== '' && v !== undefined);
    if (!visible.length) return null;
    return (
        <div style={{ marginBottom: 20 }}>
            <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: 1,
                textTransform: 'uppercase', color: 'var(--color-primary)',
                marginBottom: 8, paddingBottom: 4,
                borderBottom: '1px solid var(--color-border)',
            }}>
                {title}
            </div>
            <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 3 }}>
                {visible.map(([label, value]) => (
                    <Descriptions.Item key={label} label={label}>
                        {value}
                    </Descriptions.Item>
                ))}
            </Descriptions>
        </div>
    );
}

function ContactViewsTab({ userId }) {
    const [views, setViews]     = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        adminApi.get(`/admin/users/${userId}/contact-views`)
            .then((res) => setViews(res.data.data || []))
            .catch(() => setViews([]))
            .finally(() => setLoading(false));
    }, [userId]);

    const columns = [
        { title: 'Profile ID', dataIndex: 'viewed_profile_id', width: 140, render: (v) => <Text code>{v}</Text> },
        { title: 'Name',       render: (_, r) => `${r.first_name} ${r.last_name}` },
        { title: 'Plan',       dataIndex: 'plan_name', width: 140 },
        { title: 'Viewed At',  dataIndex: 'created_at', width: 160,
            render: (v) => dayjs(v).format('D MMM YYYY HH:mm') },
    ];

    return (
        <Table
            className="admin-table"
            dataSource={views || []}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 20, showSizeChanger: false }}
            scroll={{ x: 600 }}
            size="small"
            locale={{ emptyText: 'No contact views yet.' }}
        />
    );
}

export default function AdminUserDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { notification } = App.useApp();

    const [user, setUser]               = useState(null);
    const [loading, setLoading]         = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    // Flag modal
    const [flagModal, setFlagModal]     = useState(false);
    const [flagForm]                    = Form.useForm();

    const fetchUser = () => {
        setLoading(true);
        adminApi.get(`/admin/users/${id}`)
            .then((res) => setUser(res.data.data))
            .catch(() => notification.error({ message: 'Failed to load user', description: 'Could not retrieve user data. Please try again.' }))
            .finally(() => setLoading(false));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchUser(); }, [id]);

    const updateStatus = async (patch, successMsg, successDesc) => {
        setActionLoading(true);
        try {
            await adminApi.patch(`/admin/users/${id}/status`, patch);
            notification.success({ message: successMsg, description: successDesc });
            fetchUser();
        } catch (_) {
            notification.error({ message: 'Update failed', description: 'Could not update user status. Please try again.' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleFlag = async (values) => {
        await updateStatus(
            { is_flagged: true, flag_reason: values.reason },
            'User Flagged',
            `${user.first_name} ${user.last_name} has been flagged successfully.`
        );
        setFlagModal(false);
        flagForm.resetFields();
    };

    if (loading) return (
        <AdminLayout title="User Detail">
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#AD1457' }} spin />} />
            </div>
        </AdminLayout>
    );

    if (!user) return (
        <AdminLayout title="User Detail">
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--color-text-muted)' }}>
                User not found.
            </div>
        </AdminLayout>
    );

    const paymentColumns = [
        { title: 'Order ID',  dataIndex: 'order_id',  render: (v) => <Text code>{v}</Text> },
        { title: 'Amount',    dataIndex: 'amount',     render: (v) => `₹${v}` },
        { title: 'Status',    dataIndex: 'status',     render: (v) => (
            <Tag color={v === 'success' ? 'green' : v === 'pending' ? 'orange' : 'red'}>{v}</Tag>
        )},
        { title: 'Date',      dataIndex: 'paid_at',    render: (v) => v ? dayjs(v).format('D MMM YYYY') : '—' },
    ];

    const subColumns = [
        { title: 'Plan',   render: (_, r) => r.subscription_plan?.name || '—' },
        { title: 'Status', dataIndex: 'status', render: (v) => (
            <Tag color={v === 'active' ? 'green' : 'default'}>{v}</Tag>
        )},
        { title: 'Start',  dataIndex: 'start_date', render: (v) => dayjs(v).format('D MMM YYYY') },
        { title: 'End',    dataIndex: 'end_date',   render: (v) => dayjs(v).format('D MMM YYYY') },
    ];

    const isActive = !user.is_deleted;

    // Collect all photos across all profiles for the Photos tab
    const allPhotos = (user.profiles || []).flatMap((p) =>
        (p.profile_pictures || []).map((pic) => ({
            ...pic,
            profileName: `${p.first_name} ${p.last_name}`,
            profileId: p.profile_id,
        }))
    );

    return (
        <AdminLayout title="User Detail">
            {/* Back + actions */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/users')}>
                        Back to Users
                    </Button>
                    <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--color-text-primary)' }}>
                        <UserOutlined style={{ marginRight: 8 }} />
                        {user.first_name} {user.last_name}
                    </span>
                    <Space size={4}>
                        {isActive ? <Tag color="green">Active</Tag> : <Tag color="orange">Inactive</Tag>}
                        {user.is_flagged && <Tag color="volcano">Flagged</Tag>}
                    </Space>
                </div>
                <Space wrap>
                    <Popconfirm
                        title={isActive ? 'Delete this user?' : 'Restore this user?'}
                        description={isActive
                            ? `${user.first_name} ${user.last_name}'s account will be deactivated.`
                            : `${user.first_name} ${user.last_name}'s account will be restored.`}
                        onConfirm={() => updateStatus(
                            { is_deleted: isActive },
                            isActive ? 'User Deleted' : 'User Restored',
                            isActive
                                ? `${user.first_name} ${user.last_name}'s account has been deactivated.`
                                : `${user.first_name} ${user.last_name}'s account has been activated.`
                        )}
                        okText={isActive ? 'Delete' : 'Restore'}
                        okButtonProps={{ danger: isActive }}
                    >
                        <Button
                            icon={isActive ? <DeleteOutlined /> : <CheckCircleOutlined />}
                            loading={actionLoading}
                            danger={isActive}
                            style={!isActive ? { color: '#10B981' } : undefined}
                        >
                            {isActive ? 'Delete' : 'Restore'}
                        </Button>
                    </Popconfirm>
                    {!user.is_flagged ? (
                        <Button icon={<WarningOutlined />} danger onClick={() => setFlagModal(true)}>
                            Flag User
                        </Button>
                    ) : (
                        <Button icon={<WarningOutlined />} onClick={() => updateStatus({ is_flagged: false }, 'User Unflagged', `${user.first_name} ${user.last_name} has been unflagged.`)}>
                            Unflag
                        </Button>
                    )}
                </Space>
            </div>

            {/* Single card with all tabs */}
            <div className="admin-card">
                <Tabs
                    defaultActiveKey="account"
                    style={{ padding: '0 20px' }}
                    items={[
                        {
                            key: 'account',
                            label: 'Account Info',
                            children: (
                                <div style={{ padding: '4px 0 16px' }}>
                                    <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} size="small">
                                        <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
                                        <Descriptions.Item label="Mobile">{user.mobile_number}</Descriptions.Item>
                                        <Descriptions.Item label="Gender">{user.gender}</Descriptions.Item>
                                        <Descriptions.Item label="Joined">{dayjs(user.created_at).format('D MMM YYYY')}</Descriptions.Item>
                                        <Descriptions.Item label="Last Login">
                                            {user.last_logged_in ? dayjs(user.last_logged_in).format('D MMM YYYY HH:mm') : 'Never'}
                                        </Descriptions.Item>
                                        {user.is_flagged && (
                                            <Descriptions.Item label="Flag Reason" span={3}>
                                                <Text type="danger">{user.flag_reason || '—'}</Text>
                                            </Descriptions.Item>
                                        )}
                                    </Descriptions>
                                </div>
                            ),
                        },
                        {
                            key: 'profiles',
                            label: `Profiles (${user.profiles?.length || 0})`,
                            children: <ProfilesTab profiles={user.profiles || []} />,
                        },
                        {
                            key: 'photos',
                            label: `Photos (${allPhotos.length})`,
                            children: <PhotosTab photos={allPhotos} />,
                        },
                        {
                            key: 'payments',
                            label: `Payments (${user.payments?.length || 0})`,
                            children: (
                                <Table
                                    className="admin-table"
                                    dataSource={user.payments || []}
                                    columns={paymentColumns}
                                    rowKey="id"
                                    pagination={{ pageSize: 10, showSizeChanger: false, showQuickJumper: false }}
                                    scroll={{ x: 500 }}
                                    size="small"
                                />
                            ),
                        },
                        {
                            key: 'subscriptions',
                            label: `Subscriptions (${user.subscriptions?.length || 0})`,
                            children: (
                                <Table
                                    className="admin-table"
                                    dataSource={user.subscriptions || []}
                                    columns={subColumns}
                                    rowKey="id"
                                    pagination={{ pageSize: 10, showSizeChanger: false, showQuickJumper: false }}
                                    scroll={{ x: 500 }}
                                    size="small"
                                />
                            ),
                        },
                        {
                            key: 'contact-views',
                            label: 'Contact Views',
                            children: <ContactViewsTab userId={id} />,
                        },
                    ]}
                />
            </div>

            {/* Flag modal */}
            <Modal
                title={<span style={{ color: '#EF4444' }}><WarningOutlined /> Flag User</span>}
                open={flagModal}
                onCancel={() => { setFlagModal(false); flagForm.resetFields(); }}
                onOk={() => flagForm.submit()}
                okText="Flag User"
                okButtonProps={{ danger: true }}
                centered
            >
                <Form form={flagForm} onFinish={handleFlag} layout="vertical" style={{ marginTop: 12 }}>
                    <Form.Item name="reason" label="Flag Reason" rules={[{ required: true, message: 'Please provide a reason' }]}>
                        <Input.TextArea rows={3} placeholder="Describe the reason for flagging this user..." maxLength={500} showCount />
                    </Form.Item>
                </Form>
            </Modal>
        </AdminLayout>
    );
}

function ProfilesTab({ profiles }) {
    if (!profiles.length) return (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
            No profiles created
        </div>
    );

    const collapseItems = profiles.map((p) => ({
        key: String(p.id),
        label: (
            <Space size={8}>
                <Text strong>{p.first_name} {p.middle_name ? `${p.middle_name} ` : ''}{p.last_name}</Text>
                <Text style={{ fontSize: 12, color: 'var(--color-primary)', fontWeight: 600 }}>{p.profile_id}</Text>
                {p.is_primary && <Tag color="gold">Primary</Tag>}
                {p.is_deleted && <Tag color="red">Deleted</Tag>}
                <Tag color={p.gender === 'Male' ? 'blue' : p.gender === 'Female' ? 'pink' : 'default'}>{p.gender}</Tag>
            </Space>
        ),
        children: <ProfileDetail profile={p} />,
    }));

    return (
        <div style={{ padding: '0 0 16px' }}>
            <Collapse accordion items={collapseItems} />
        </div>
    );
}

function PhotosTab({ photos }) {
    if (!photos.length) return (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
            No photos uploaded
        </div>
    );

    // Group photos by profile
    const grouped = photos.reduce((acc, pic) => {
        const key = pic.profileId || pic.profileName;
        if (!acc[key]) acc[key] = { label: `${pic.profileName} (${pic.profileId})`, pics: [] };
        acc[key].pics.push(pic);
        return acc;
    }, {});

    return (
        <div style={{ padding: '8px 0 16px' }}>
            {Object.values(grouped).map((group) => (
                <div key={group.label} style={{ marginBottom: 24 }}>
                    <div style={{
                        fontSize: 11, fontWeight: 700, letterSpacing: 1,
                        textTransform: 'uppercase', color: 'var(--color-primary)',
                        marginBottom: 12, paddingBottom: 4,
                        borderBottom: '1px solid var(--color-border)',
                    }}>
                        {group.label}
                    </div>
                    <Image.PreviewGroup>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                            {group.pics.map((pic) => (
                                <Image
                                    key={pic.id}
                                    src={`${process.env.REACT_APP_IMAGE_URL}/${pic.image_path}`}
                                    width={120}
                                    height={120}
                                    style={{ objectFit: 'cover', borderRadius: 8, border: '1px solid var(--color-border)' }}
                                />
                            ))}
                        </div>
                    </Image.PreviewGroup>
                </div>
            ))}
        </div>
    );
}

function ProfileDetail({ profile: p }) {
    const edu  = p.education_occupation;
    const phy  = p.physical_lifestyle;
    const srb  = p.socio_religious_background;
    const fam  = p.family_information;
    const con  = p.contact_information;
    const exps = p.expectations || [];

    return (
        <div style={{ padding: '12px 4px' }}>
            {/* Basic Info */}
            <ProfileSection title="Basic Information" items={[
                ['First Name',         p.first_name],
                ['Middle Name',        p.middle_name],
                ['Last Name',          p.last_name],
                ['Gender',             p.gender],
                ['Date of Birth',      p.date_of_birth ? dayjs(p.date_of_birth).format('D MMM YYYY') : null],
                ['Time of Birth',      p.time_of_birth],
                ['Place of Birth',     p.place_of_birth],
                ['Marital Status',     p.marital_status],
                ['Current Location',   p.current_location],
                ['Profile Created For',p.profile_created_for],
                ['Registered On',      p.profile_registered_date ? dayjs(p.profile_registered_date).format('D MMM YYYY') : null],
            ]} />

            {/* Physical & Lifestyle */}
            {phy && <ProfileSection title="Physical & Lifestyle" items={[
                ['Height',          phy.height],
                ['Weight',          phy.weight ? `${phy.weight} kg` : null],
                ['Body Type',       phy.body_type],
                ['Physical Status', phy.physical_status],
                ['Complexion',      phy.complexion],
                ['Blood Group',     phy.blood_group],
                ['Diet',            phy.diet],
                ['Smoke',           phy.smoke],
                ['Drink',           phy.drink],
            ]} />}

            {/* Education & Occupation */}
            {edu && <ProfileSection title="Education & Occupation" items={[
                ['Education Category', edu.education_category],
                ['Highest Education',  edu.highest_education],
                ['Education Detail',   edu.education_detail],
                ['Occupation',         edu.occupation],
                ['Occupation Detail',  edu.occupation_detail],
                ['Employed In',        edu.employed_in],
                ['Annual Income',      edu.annual_income_range],
            ]} />}

            {/* Socio-Religious Background */}
            {srb && <ProfileSection title="Socio-Religious Background" items={[
                ['Religion',      srb.religion],
                ['Denomination',  srb.denomination],
                ['Caste',         srb.caste],
                ['Subcaste',      srb.subcaste],
                ['Division',      srb.division],
                ['Mother Tongue', srb.mother_tongue],
            ]} />}

            {/* Family Information */}
            {fam && <ProfileSection title="Family Information" items={[
                ['Family Values',         fam.family_values],
                ['Family Type',           fam.family_type],
                ['Family Origin',         fam.family_origin],
                ['Father Name',           fam.father_name],
                ['Father Alive',          fam.father_alive != null ? (fam.father_alive ? 'Yes' : 'No') : null],
                ['Father Occupation',     fam.father_occupation],
                ['Mother Name',           fam.mother_name],
                ['Mother Alive',          fam.mother_alive != null ? (fam.mother_alive ? 'Yes' : 'No') : null],
                ['Mother Occupation',     fam.mother_occupation],
                ['Brothers',              fam.no_of_brothers != null ? `${fam.no_of_brothers} (${fam.no_of_brothers_married ?? 0} married)` : null],
                ['Sisters',               fam.no_of_sisters  != null ? `${fam.no_of_sisters} (${fam.no_of_sisters_married ?? 0} married)` : null],
            ]} />}

            {/* Contact Information */}
            {con && <ProfileSection title="Contact Information" items={[
                ['Mobile',            con.mobile_number],
                ['Alternative Mobile',con.alternative_mobile_number],
                ['WhatsApp',          con.whatsapp_number],
                ['Email',             con.email_address],
                ['Door No',           con.door_no],
                ['Address Line 1',    con.address_1],
                ['Address Line 2',    con.address_2],
                ['Landmark',          con.landmark],
                ['City',              con.city],
                ['District',          con.district],
                ['State',             con.state],
                ['Country',           con.country],
                ['Postal Code',       con.postal_code],
            ]} />}

            {/* Expectations */}
            {exps.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                    <div style={{
                        fontSize: 11, fontWeight: 700, letterSpacing: 1,
                        textTransform: 'uppercase', color: 'var(--color-primary)',
                        marginBottom: 8, paddingBottom: 4,
                        borderBottom: '1px solid var(--color-border)',
                    }}>
                        Partner Expectations
                    </div>
                    <Table
                        size="small"
                        dataSource={exps}
                        rowKey="id"
                        pagination={false}
                        scroll={{ x: 480 }}
                        columns={[
                            { title: 'Type',      dataIndex: 'preference_type',  width: 120 },
                            { title: 'Attribute', dataIndex: 'preference_key',   width: 160 },
                            { title: 'Value',     dataIndex: 'preference_value' },
                            { title: 'Strict',    dataIndex: 'strict', width: 70,
                                render: (v) => v ? <Tag color="red">Yes</Tag> : <Tag color="default">No</Tag> },
                        ]}
                    />
                </div>
            )}

            {/* Verifications */}
            {p.verifications && (
                <div style={{ marginBottom: 20 }}>
                    <div style={{
                        fontSize: 11, fontWeight: 700, letterSpacing: 1,
                        textTransform: 'uppercase', color: 'var(--color-primary)',
                        marginBottom: 8, paddingBottom: 4,
                        borderBottom: '1px solid var(--color-border)',
                    }}>
                        Verifications
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        {Object.entries(verificationLabels).map(([key, label]) => (
                            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Text style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{label}:</Text>
                                {verificationBadge(p.verifications[key])}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}


